import React, { useState, useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Card, Button, Label, TextInput } from "flowbite-react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { auth } from "../../firebaseConfig";
import { signOut } from "firebase/auth";
import { USER, INVITE_ACCEPT, ME } from "../../lib/urls";
import { useStore } from "../../store/index";
import InvitationExpired from "../../components/Onboarding/InvitationExpired";

const RegisterForm = ({}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const uid = useStore((state) => state.uid);
  const setLogout = useStore((state) => state.setLogout);

  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("inviteToken");

  const setUser = useStore((state) => state.setUser);
  const setCompany = useStore((state) => state.setCompany);
  const setOnboardingRequired = useStore(
    (state) => state.setOnboardingRequired
  );

  useEffect(() => {
    console.log("useeffect");
    if (uid) {
      signOut(auth);
      setLogout();
      toast.success("Logged out of current session");
    }
  }, []);

  const joinHandler = async (firebaseToken) => {
    setOnboardingRequired(false);
    setIsLoading(true);
    try {
      const r = await fetch(INVITE_ACCEPT(inviteToken), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${firebaseToken}`, // Uses passed Firebase token
        },
      });

      if (!r.ok) {
        const data = await r.json();
        setError(data?.detail);
        throw new Error(data?.detail);
      }

      await fetchMe(firebaseToken);
      toast.success("Joined organization successfully");
      console.log("Joined successfully, redirecting to jobs...");

      setTimeout(() => {
        navigate("/jobs");
      }, 1000);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMe = async (firebaseToken) => {
    try {
      const response = await fetch(ME, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${firebaseToken}`,
        },
      });

      if (response.ok) {
        const {
          user: { id, ...data },
          company,
        } = await response.json();

        setUser(data);
        setCompany(company);
        return data;
      }
    } catch (error) {
      console.error("Error fetching me:", error);
    }
    if (error && inviteToken) {
      return (
        <InvitationExpired message={"This invitation has already been used"} />
      );
    }
  };

  if (!uid) {
    const handleRegister = async (e) => {
      e.preventDefault();
      setIsLoading(true);

      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        const user = userCredential.user;

        const r = await fetch(USER, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: user.uid,
            email: email,
            name: name,
          }),
        });

        if (!r.ok) {
          throw new Error((await r.json())?.detail);
        }

        if (inviteToken) {
          const firebaseToken = await user.getIdToken();
          await joinHandler(firebaseToken);
        } else {
          navigate(
            location?.search.replace("?redirect=", "") || "/onboarding/company"
          );
        }
        toast.success("User created successfully");
      } catch (error) {
        setError(error.message);
        toast.error(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <Card className="w-full max-w-2xl p-6">
          <h1
            className="text-h4 font-bold mb-4"
          >
            Create Your Account
          </h1>
          <p className="mb-6">
            <strong>Welcome to June!</strong> June is your AI Interviewer,
            designed to streamline and enhance your hiring process by making
            candidate screening faster and more efficient.
          </p>
          <form>
            <div className="mb-4">
              <Label htmlFor="adminName" value="Your Name" />
              <div className="mb-1"></div>
              <TextInput
                id="adminName"
                name="adminName"
                type="text"
                placeholder="Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="mb-4">
              <Label htmlFor="companyEmail" value="Your Company Email" />
              <div className="mb-1"></div>
              <TextInput
                id="companyEmail"
                name="companyEmail"
                type="email"
                placeholder="Your Company Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="mb-8">
              <Label htmlFor="password" value="Password" />
              <div className="mb-1"></div>
              <TextInput
                id="password"
                name="password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="flex justify-between">
              <Button
                as={Link}
                to={`/login${location?.search || ""}`}
                type="button"
                style={{
                  backgroundColor: "transparent",
                  borderColor: "#881BC8",
                  color: "#881BC8",
                }}
              >
                Back
              </Button>
              <Button
                onClick={handleRegister}
                style={{ backgroundColor: "#881BC8" }}
                disabled={isLoading}
                className="flex items-center justify-center"
                data-cy="create-account-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Account"
                )}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    );
  } else {
    console.log(
      "It looks like you tried to register while already logged in\nThats ok, i'll log you out."
    );
    signOut(auth);
    setLogout();
  }
};

export default RegisterForm;
